import { Box, Group, Stack, Text } from '@mantine/core';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { SlidePageCanvas } from './SlidePageCanvas';
import { resolveMainPage } from './resolveMainPage';

export interface SectionSlideVisual {
  /** 1-based PDF page number in the slide deck. */
  slide_page: number;
  sort_order: number;
}

export interface SectionSlidesProps {
  doc: PDFDocumentProxy;
  /** The current section's associated slides (sorted here by sort_order). */
  visuals: SectionSlideVisual[];
  /**
   * The page shown large. When null/absent (or not among the section's
   * visuals), the first associated slide is used.
   */
  selectedPage?: number | null;
  /**
   * When provided, the thumbnail filmstrip is interactive — clicking a
   * thumbnail invokes this with that page (coach side). When absent the
   * filmstrip is read-only (participant side).
   */
  onSelectPage?: (page: number) => void;
  /** Native render width for the large (primary) slide. */
  largeWidth?: number;
  /** Native render width for the filmstrip thumbnails. */
  thumbnailWidth?: number;
  /**
   * MED-1030 AC2 — render the thumbnail filmstrip beneath the main slide.
   * Coach keeps it (true, default) so the coach can promote slides; the
   * participant passes false (read-only, can't navigate → filmstrip is just
   * noise) and sees the current slide only.
   */
  showFilmstrip?: boolean;
}

/**
 * "Main slide + filmstrip" presentation of a section's authored slides, shared
 * by the coach whiteboard and participant slide card (E3.3a).
 *
 * The large slide is `selectedPage` (the coach-selected "main" slide), falling
 * back to the section's first associated slide. All of the section's slides
 * appear in a filmstrip beneath it with the current main highlighted. On the
 * coach side `onSelectPage` makes the filmstrip clickable so the coach can
 * promote any slide to the main position; that selection is persisted +
 * broadcast so every attendee (participant filmstrip is read-only) sees the
 * same main slide — "shared with all".
 */
export function SectionSlides({
  doc,
  visuals,
  selectedPage,
  onSelectPage,
  largeWidth = 720,
  thumbnailWidth = 96,
  showFilmstrip = true,
}: SectionSlidesProps) {
  const sorted = visuals.slice().sort((a, b) => a.sort_order - b.sort_order);
  if (sorted.length === 0) return null;

  // The main slide is the coach-selected page when it belongs to this section,
  // else the section's first associated slide.
  const mainPage = resolveMainPage(visuals, selectedPage) ?? sorted[0].slide_page;

  const interactive = typeof onSelectPage === 'function';

  return (
    <Stack gap="sm" align="center" w="100%" data-testid="section-slides">
      <Box
        style={{
          width: '100%',
          maxWidth: largeWidth,
          borderRadius: 8,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          background: '#FFFFFF',
        }}
      >
        <SlidePageCanvas
          doc={doc}
          pageNum={mainPage}
          width={largeWidth}
          ariaLabel={`Slide page ${mainPage}`}
          style={{ width: '100%' }}
        />
      </Box>

      {showFilmstrip && sorted.length > 1 && (
        <Group
          gap="xs"
          justify="center"
          wrap="wrap"
          data-testid="section-slides-thumbnails"
        >
          {sorted.map(v => {
            const isMain = v.slide_page === mainPage;
            const thumb = (
              <>
                <SlidePageCanvas
                  doc={doc}
                  pageNum={v.slide_page}
                  width={thumbnailWidth}
                  ariaLabel={`Slide page ${v.slide_page}`}
                  style={{ width: '100%' }}
                />
                <Text
                  component="span"
                  style={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    background: 'rgba(0,0,0,0.65)',
                    color: 'white',
                    fontSize: 9,
                    padding: '1px 4px',
                    borderRadius: 3,
                    fontWeight: 600,
                    lineHeight: 1.4,
                  }}
                >
                  {v.slide_page}
                </Text>
              </>
            );

            const sharedStyle = {
              width: thumbnailWidth,
              position: 'relative' as const,
              borderRadius: 4,
              overflow: 'hidden',
              padding: 0,
              background: '#FFFFFF',
              border: isMain
                ? '2px solid var(--mantine-color-medleyGreen-6)'
                : '1px solid rgba(0,0,0,0.12)',
            };

            return interactive ? (
              <button
                key={v.slide_page}
                type="button"
                onClick={() => onSelectPage?.(v.slide_page)}
                aria-pressed={isMain}
                aria-label={`Show slide ${v.slide_page} as the main slide`}
                style={{ ...sharedStyle, cursor: 'pointer' }}
                data-testid={`section-slides-thumb-${v.slide_page}`}
                data-active={isMain ? 'true' : 'false'}
              >
                {thumb}
              </button>
            ) : (
              <Box
                key={v.slide_page}
                style={sharedStyle}
                data-testid={`section-slides-thumb-${v.slide_page}`}
                data-active={isMain ? 'true' : 'false'}
              >
                {thumb}
              </Box>
            );
          })}
        </Group>
      )}
    </Stack>
  );
}
